import { faCaretDown, faCaretRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex } from 'components/Flex';
import { useMemo, useState } from 'react';
import { Button } from '../Button';
import { Checkbox } from '../Checkbox';
import type { IOption } from './nestedSelectTypes';
import { flattenOptions } from './utilities';

interface Props {
  option: IOption;
  selectedOptions: IOption[];
  setSelectedOptions: (options: IOption[]) => void;
  disabled?: boolean;
  isOneOptionSelect?: boolean;
  /** Z isOneOptionSelect: wybór dowolnego węzła (gałąź lub liść), bez zaznaczania dzieci. */
  singleSelectAnyNode?: boolean;
  setIsSelectOpen: (isOpen: boolean) => void;
}

export const Option = ({
  option,
  selectedOptions,
  setSelectedOptions,
  disabled,
  isOneOptionSelect,
  singleSelectAnyNode,
  setIsSelectOpen
}: Props) => {
  const [isOpen, setIsOpen] = useState(true);

  const flatSelected = useMemo(
    () => flattenOptions(selectedOptions),
    [selectedOptions]
  );

  const branchLeaves = useMemo(
    () => (option.options ? flattenOptions(option.options) : undefined),
    [option.options]
  );

  const selectedContainsLeaf = (leafValue: string | undefined) =>
    !!flatSelected.some((s) => s.value === leafValue);

  const isExactNodeSelected = (nodeValue: string | undefined) =>
    !!nodeValue &&
    selectedOptions.some((selected) => selected.value === nodeValue);

  const isEveryItemSelected = !!branchLeaves?.every((item) =>
    selectedContainsLeaf(item.value)
  );

  const isNodeChecked = (node: IOption) => {
    if (isOneOptionSelect && singleSelectAnyNode) {
      return isExactNodeSelected(node.value);
    }
    if (node.options) {
      return isEveryItemSelected;
    }
    return selectedContainsLeaf(node.value);
  };

  const handleOptionOnChange = () => {
    if (isOneOptionSelect && singleSelectAnyNode) {
      setSelectedOptions([option]);
      setIsSelectOpen(false);
      return;
    }

    if (option.options) {
      const leaves = branchLeaves ?? flattenOptions(option.options);
      if (isEveryItemSelected) {
        setSelectedOptions([
          ...flatSelected.filter(
            (item) =>
              !leaves?.find(
                (leaf) => leaf.value === item.value
              )
          )
        ]);
      } else {
        setSelectedOptions([
          ...selectedOptions,
          ...(leaves?.filter(
            (item) =>
              !selectedOptions.find(
                (selectedOption) => selectedOption.value === item.value
              )
          ) ?? [])
        ]);
      }
    } else {
      if (isOneOptionSelect) {
        setSelectedOptions([option]);
        setIsSelectOpen(false);
      } else {
        if (
          selectedOptions.find(
            (selectedOption) => selectedOption.value === option.value
          )
        ) {
          setSelectedOptions([
            ...selectedOptions.filter(
              (filterOption) => filterOption.value !== option.value
            )
          ]);
        } else {
          setSelectedOptions([...selectedOptions, option]);
        }
      }
    }
  };

  return (
    <Flex p={1} flexDirection="column">
      <Flex alignItems="center" pl={option.options ? 0 : '40px'}>
        {option.options && (
          <Button
            variant="secondary"
            icon={
              <FontAwesomeIcon
                color="grey"
                icon={isOpen ? faCaretDown : faCaretRight}
              />
            }
            onClick={() => {
              setIsOpen((prevIsOpen) => !prevIsOpen);
            }}
          />
        )}
        {isOneOptionSelect && !singleSelectAnyNode ? (
          !option.options && (
            <Checkbox
              variant="tertiary"
              disabled={disabled ? true : option.isDisabled}
              checked={isNodeChecked(option)}
              onChange={handleOptionOnChange}
            />
          )
        ) : (
          <Checkbox
            variant="tertiary"
            disabled={disabled ? true : option.isDisabled}
            checked={isNodeChecked(option)}
            onChange={handleOptionOnChange}
          />
        )}

        {option.label}
      </Flex>

      {isOpen && (
        <Flex pl={4} flexDirection="column">
          {option.options?.map((nestedOption) => (
            <Option
              disabled={disabled}
              key={nestedOption.label}
              option={nestedOption}
              selectedOptions={selectedOptions}
              setSelectedOptions={setSelectedOptions}
              isOneOptionSelect={isOneOptionSelect}
              singleSelectAnyNode={singleSelectAnyNode}
              setIsSelectOpen={setIsSelectOpen}
            />
          ))}
        </Flex>
      )}
    </Flex>
  );
};
